import axios from 'axios';
import { getAuthorazieHeader } from './service';
import { url } from './config';

const BASE_URL = `${url}/approvalWorkflow`;

export interface WorkflowLevelConfig {
  level: number;
  email: string;
  role: string;
  slaHours: number;
}

export interface WorkflowConfigPayload {
  name: string;
  entityType: string;
  mode: 'SINGLE' | 'SEQUENTIAL' | 'PARALLEL';
  levels: WorkflowLevelConfig[];
  escalation?: {
    enabled?: boolean;
    contactEmail?: string;
    contactRole?: string;
    triggerAfterHours?: number;
  };
  postApprovalNotification?: {
    enabled?: boolean;
    recipients?: string[];
    ccRoles?: string[];
    template?: string;
  };
}

export interface ApprovalRequestPayload {
  name?: string;
  workflowConfigId?: string;
  workflowConfig?: WorkflowConfigPayload;
  entityType: string;
  entityId: string;
  entitySnapshot?: Record<string, unknown>;
  submittedBy?: string;
  mode?: 'SINGLE' | 'SEQUENTIAL' | 'PARALLEL';
  levels?: WorkflowLevelConfig[];
  escalation?: {
    enabled?: boolean;
    contactEmail?: string;
    contactRole?: string;
    triggerAfterHours?: number;
  };
  postApprovalNotification?: {
    enabled?: boolean;
    recipients?: string[];
    ccRoles?: string[];
    template?: string;
  };
}

export interface ApprovalActionPayload {
  level?: number;
  approverEmail?: string;
  comments?: string;
  submittedBy?: string;
  cancelledBy?: string;
  entitySnapshot?: Record<string, unknown>;
  updatedSnapshot?: Record<string, unknown>;
}

export interface ApprovalRequestFilterParams {
  status?: string;
  entityType?: string;
  approverEmail?: string;
  submittedBy?: string;
  page?: number;
  limit?: number;
}

export interface ApproverInboxParams {
  status?: string;
  entityType?: string;
  page?: number;
  limit?: number;
}

export interface SchedulePayload {
  Eval_Window?: string;
  Interval?: string;
  Cooldown?: string;
}

export interface ResourcePayload {
  type: string;
  name: string;
  identifier?: string;
}

export interface VariablePayload {
  key?: string;
  variableName?: string;
  variableIdentifier?: string;
  operator?: string;
  value: any;
}

export interface ESVariablePayload {
  variableName?: string;
  variableIdentifier: string;
  value: any;
}

export interface ESQueryPayload {
  indexName: string;
  query: string;
  variables?: any[];
  Variables?: any;
  keysToExtract?: any[];
}

export interface RuleConfigDto {
  _id?: string;
  Domain: string;
  Component: string;
  Layer_Tool?: string;
  Metric_KPI: string;
  EWS_Signal_Name: string;
  Detection_Logic_Trend_Pattern?: string;
  Risk_Level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  Base_ES_Query?: any;
  Final_ES_Query?: any;
  variables?: VariablePayload[];
  Variables?: any;
  Data_Source?: string;
  Rule_Name?: string;
  Notes?: string;
  Is_Deleted?: boolean;
  Is_Active?: boolean;
  Is_Approved?: boolean;
  Approval_Id?: string | null;
  Workflow_Id?: string | null;
  Created_By?: string;
  Created_Date?: string;
  Updated_By?: string;
  Updated_Date?: string;
  Reviewed_By?: string;
  Reviewed_Date?: string;
  Schedule: SchedulePayload;
  Resources: ResourcePayload[];
  Handler_Type?: string;
  Handler?: string;
  Handler_status?: 'MAPPED'| 'UNMAPPED';
  action?: string;
  Test_Run_Status?: 'NOT_RUN' | 'SKIPPED' | 'PASSED' | 'FAILED';
}

export type RuleConfigPayload = Omit<RuleConfigDto, '_id' | 'Created_Date' | 'Updated_Date'>;

export interface EwsRulesMetadataDto {
  _id?: string;
  Domain: string;
  Component: string;
  Layer_Tool?: string;
  Metric_KPI: string;
  EWS_Signal_Name: string;
  Detection_Logic_Trend_Pattern?: string;
  Risk_Level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  Data_Source?: string;
  Base_ES_Query?: any;
  variables?: VariablePayload[];
}

// =========================================================================
// RULE CONFIGURATION APIS
// =========================================================================

export const saveRuleConfig = async (payload: RuleConfigPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/rule-configs`, payload, { headers });
  return res.data;
};

export const getRuleConfigs = async () => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/rule-configs`, { headers });
  return res.data;
};

export const getRuleConfigById = async (id: string) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/rule-configs/${id}`, { headers });
  return res.data;
};

export const updateRuleConfig = async (id: string, payload: Partial<RuleConfigPayload>) => {
  const headers = getAuthorazieHeader();
  const res = await axios.put(`${BASE_URL}/rule-configs/${id}`, payload, { headers });
  return res.data;
};

export const updateServiceMap = async (name: string, payload: Partial<RuleConfigPayload>) => {
  const headers = getAuthorazieHeader();
  const res = await axios.put(`${BASE_URL}/service-map/${name}`, payload, { headers });
  return res.data;
};

export const deleteRuleConfig = async (id: string) => {
  const headers = getAuthorazieHeader();
  const res = await axios.delete(`${BASE_URL}/rule-configs/${id}`, { headers });
  return res.data;
};

// =========================================================================
// WORKFLOW CONFIGURATION APIS
// =========================================================================

export const saveWorkflowConfig = async (payload: WorkflowConfigPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/workflow-configs`, payload, { headers });
  return res.data;
};

export const getWorkflowConfigs = async (params?: { entityType?: string; isActive?: boolean; page?: number; limit?: number }) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/workflow-configs`, { headers, params });
  return res.data;
};

export const getWorkflowConfigById = async (id: string) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/workflow-configs/${id}`, { headers });
  return res.data;
};

export const updateWorkflowConfig = async (id: string, payload: Partial<WorkflowConfigPayload>) => {
  const headers = getAuthorazieHeader();
  const res = await axios.put(`${BASE_URL}/workflow-configs/${id}`, payload, { headers });
  return res.data;
};

export const deleteWorkflowConfig = async (id: string) => {
  const headers = getAuthorazieHeader();
  const res = await axios.delete(`${BASE_URL}/workflow-configs/${id}`, { headers });
  return res.data;
};

// =========================================================================
// APPROVAL REQUEST & REVIEWER APIS
// =========================================================================

export const createApprovalRequest = async (payload: ApprovalRequestPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/approval-requests`, payload, { headers });
  return res.data;
};

export const getApprovalRequests = async (params?: ApprovalRequestFilterParams) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/approval-requests`, { headers, params });
  return res.data;
};

export const getApproverInbox = async (params?: ApproverInboxParams) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/approval-requests/inbox`, { headers, params });
  return res.data;
};

export const getApprovalRequestById = async (id: string) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/approval-requests/${id}`, { headers });
  return res.data;
};

export const approveRequest = async (id: string, payload?: ApprovalActionPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/approval-requests/${id}/approve`, payload || {}, { headers });
  return res.data;
};

export const rejectRequest = async (id: string, payload?: ApprovalActionPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/approval-requests/${id}/reject`, payload || {}, { headers });
  return res.data;
};

export const resubmitRequest = async (id: string, payload?: ApprovalActionPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/approval-requests/${id}/resubmit`, payload || {}, { headers });
  return res.data;
};

export const cancelRequest = async (id: string, payload?: ApprovalActionPayload) => {
  const headers = getAuthorazieHeader();
  const res = await axios.post(`${BASE_URL}/approval-requests/${id}/cancel`, payload || {}, { headers });
  return res.data;
};

export const getRequestHistory = async (id: string) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.get(`${BASE_URL}/approval-requests/${id}/history`, { headers });
    return res.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return { success: true, data: { history: [] } };
    }
    throw error;
  }
};

export const getEwsRulesMetadata = async () => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/rules-metadata`, { headers });
  return res.data;
};

export const getEwsResources = async (resourceType: string) => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/resources`, {
    headers,
    params: { resourceType },
    data: { resourceType }
  });
  return res.data;
};

export interface EwsApproverDetailDto {
  _id: string;
  email: string;
  role: string;
  sla: string;
}

export const getEwsApproverDetails = async () => {
  const headers = getAuthorazieHeader();
  const res = await axios.get(`${BASE_URL}/approver-details`, { headers });
  return res.data;
};

export interface RuleTestPayload {
  ruleConfigId?: string;
  EWS_Signal_Name?: string;
  signal?: string;
  Base_ES_Query?: any;
  variables?: any[];
  Schedule?: SchedulePayload;
  Resources?: ResourcePayload[];
  evalWindow?: string;
}

export const runRuleTest = async (payload: RuleTestPayload | any) => {
  const headers = getAuthorazieHeader();
  const id = payload?.ruleConfigId || payload?._id || '';

  if (id) {
    try {
      const res = await axios.post(`${BASE_URL}/rules/test-run/${id}`, payload, { headers });
      return res.data;
    } catch (e1: any) {
      if (e1?.response?.status === 404) {
        try {
          const res = await axios.post(`${BASE_URL}/rules/test-run/${id}`, payload, { headers });
          return res.data;
        } catch (e2: any) {
          if (e2?.response?.status === 404) {
            try {
              const res = await axios.post(`${BASE_URL}/rule-configs/${id}/test`, payload, { headers });
              return res.data;
            } catch (e3: any) {
              if (e3?.response?.status === 404) {
                const res = await axios.post(`${BASE_URL}/rules/test-run`, payload, { headers });
                return res.data;
              }
              throw e3;
            }
          }
          throw e2;
        }
      }
      throw e1;
    }
  }

  const res = await axios.post(`${BASE_URL}/rules/test-run`, payload, { headers });
  return res.data;
};



